package com.taocent.simple.compose.component.richtext.core.internal.clipboard

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.platform.ClipEntry
import androidx.compose.ui.platform.Clipboard
import androidx.compose.ui.platform.ClipboardItem
import androidx.compose.ui.text.AnnotatedString
import com.taocent.simple.compose.component.richtext.core.internal.serialization.AnnotatedStringToHtml
import com.taocent.simple.compose.component.richtext.core.internal.serialization.RichTextHtmlConfig
import com.taocent.simple.compose.component.richtext.core.internal.serialization.HtmlToAnnotatedString
import kotlin.js.ExperimentalWasmJsInterop
import kotlin.js.Promise
import kotlin.js.js
import kotlinx.coroutines.await

@OptIn(ExperimentalComposeUiApi::class)
actual fun AnnotatedString.toClipEntry(): ClipEntry =
    ClipEntry.withPlainText(text)

/**
 * 富文本版写入:同时写 `text/plain` + `text/html` 两种 MIME。
 *
 * 实现:`js("new ClipboardItem({...})")` 构造 `ClipboardItem`,传入 `ClipEntry(Array<ClipboardItem>)` 构造器。
 * `text/html` 部分用 [AnnotatedStringToHtml.toHtml] 序列化。
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
actual fun AnnotatedString.toRichClipEntry(config: RichTextHtmlConfig): ClipEntry {
    val plain = text
    val html = AnnotatedStringToHtml.toHtml(this, config)
    return toRichClipEntryInternal(plain, html)
}

/**
 * Low-level 重载:接收分离的 plain text + html 字符串,**主动**异步写 `navigator.clipboard.write`。
 *
 * **关键**:本函数签名是 suspend,内部**主动**调 `navigator.clipboard.write([clipboardItem])`
 * 异步 Promise(不依赖 commonMain `clipboard.setClipEntry` 路径)。
 *
 * 为什么必须由 web actual 主动写:
 *   - commonMain `clipboard.setClipEntry(entry)` 内部走 `navigator.clipboard.write(items)`,
 *     但 `navigator.clipboard.write` **必须在 user gesture 同步栈内发起**(Chrome / WebView
 *     限制)。commonMain suspend 协程内 await 时已经**脱离** user gesture 同步栈,
 *     write Promise 会被浏览器拒绝 / Permission denied
 *   - 修复:把 `navigator.clipboard.write` 主动调用挪到 `toRichClipEntry` 实际实现,
 *     配合 BlockRichTextEditor 浮动工具栏 onCopy click handler 同步栈发起(参考
 *     `tryStartUserGestureClipboardWrite` 机制)
 *
 * 用途:emoji 写剪贴板时 plain 用 shortcode 版(短码可读),html 用原版 annotated
 * 序列化(让序列化器能识别 emoji annotation 段输出 `<span data-emoji-id>`)。
 * 详见 commonMain `expect suspend fun toRichClipEntry(plainText, html, config)` 的 KDoc。
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
actual suspend fun toRichClipEntry(
    plainText: String,
    html: String,
    config: RichTextHtmlConfig,
): ClipEntry = toRichClipEntryInternal(plainText, html)

/**
 * Web 端 `setRichTextClipEntry`:调 `clipboard.setClipEntry(entry)`。
 *
 * **为什么 Web 不需要 noop**:
 *   Web 端 `toRichClipEntry(plain, html, config)` 内部**只构造** ClipEntry(ClipboardItem
 *   数组),**不主动**调 `navigator.clipboard.write` —— 那是 `setClipEntry` 内部的事。
 *   Compose 库 `clipboard.setClipEntry(entry)` 内部走 `navigator.clipboard.write([clipboardItem])`,
 *   Promise 在 user gesture 同步栈内发起,正确处理双 MIME(text/plain + text/html 都保留在
 *   ClipboardItem 内)。
 *
 *   调 `setClipEntry` 的目的:**主动触发** `navigator.clipboard.write` 写入系统剪贴板
 *   + 让 FakeClipboard 测试通过。
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
actual suspend fun setRichTextClipEntry(clipboard: Clipboard, entry: ClipEntry) {
    clipboard.setClipEntry(entry)
}

/**
 * 内部共享实现:用 plain + html 字符串构造 `ClipboardItem { text/plain + text/html }`,
 * 包装为 `Array<ClipboardItem>` 传给 `ClipEntry` 构造器。
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
private fun toRichClipEntryInternal(plain: String, html: String): ClipEntry {
    val items: Array<ClipboardItem> = js(
        "[new ClipboardItem({" +
            "'text/plain': new Blob([plain], { type: 'text/plain' }), " +
            "'text/html': new Blob([html], { type: 'text/html' })" +
        "})]"
    ).unsafeCast<Array<ClipboardItem>>()
    return ClipEntry(items)
}

@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
actual suspend fun ClipEntry.toAnnotatedStringOrNull(): AnnotatedString? {
    val item = clipboardItems.firstOrNull() ?: return null
    val blob = try {
        item.getType("text/plain".toJsString()).await()
    } catch (_: Throwable) {
        return null
    }
    val raw = try {
        js("blob.text()").unsafeCast<Promise<String>>().await()
    } catch (_: Throwable) {
        return null
    }
    return raw.takeIf { it.isNotEmpty() }?.let { AnnotatedString(it) }
}

/**
 * 富文本版读取:优先 `text/html` → [HtmlToAnnotatedString.parse] 还原 spans / paragraph / emoji / hyperlink;
 * 无 `text/html` 时返回 null,调用方 fallback [toAnnotatedStringOrNull]。
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalWasmJsInterop::class)
actual suspend fun ClipEntry.toRichAnnotatedStringOrNull(): AnnotatedString? {
    val item = clipboardItems.firstOrNull() ?: return null
    // 尝试读取 text/html(有则用 HtmlToAnnotatedString 解析)
    val htmlBlob = try {
        item.getType("text/html".toJsString()).await()
    } catch (_: Throwable) {
        return null
    }
    val html = try {
        js("htmlBlob.text()").unsafeCast<Promise<String>>().await()
    } catch (_: Throwable) {
        return null
    }
    if (html.isEmpty()) return null
    return try {
        HtmlToAnnotatedString.parse(html)
    } catch (_: Throwable) {
        null
    }
}
